<template>
  <div class="flex min-h-screen max-h-screen flex-col">
    <div v-if="!isLoading" class="flex-grow">
      <div class="flex mx-auto p-4 w-full justify-center" v-if="getBodyPrint">
        <div class="flex w-full">
          <!-- Back Image -->
          <div class="w-auto">
            <div class="border border-white">
              <div class="flex flex-col items-center">
                <h2 class="text-blue-800 mb-2">
                  {{ $t("blur_edit.back_image") }}
                </h2>
                <div
                  class="relative border-2 border-gray-600"
                  :style="getContainerStyle('back')"
                  @mousedown="deselectAll"
                >
                  <img
                    v-if="getImageBack"
                    ref="backImage"
                    :src="getImageBack"
                    :style="getImageStyle('back')"
                    alt="Back image"
                    @load="onImageLoad('back', $event)"
                  />
                  <blur-box
                    v-if="backBlurBox && backImageDimensions"
                    :box="backBlurBox"
                    :selected="selectedImage === 'back'"
                    :simple-mode="isSimpleMode"
                    :display-scale="getDisplayScale('back')"
                    :image-dimensions="backImageDimensions"
                    :original-box="originalBackBox"
                    @select="selectedImage = 'back'"
                    @update="pendingBackBlurData = { faces: [$event] }"
                  />
                </div>
                <button
                  v-if="!backBlurBox && backImageDimensions && !isSimpleMode"
                  class="mt-2 py-2 px-4 bg-blue-800 text-white"
                  @click="addBlurBox('back')"
                >
                  {{ $t("blur_edit.add_blur_box") }}
                </button>
                <button
                  v-else-if="
                    backBlurBox && backImageDimensions && !isSimpleMode
                  "
                  class="mt-2 py-2 px-4 bg-red-700 text-white"
                  @click="removeBlurBox()"
                >
                  {{ $t("blur_edit.remove_blur_box") }}
                </button>
              </div>
            </div>
          </div>

          <!-- Side Image -->
          <div class="w-auto">
            <div class="border border-white">
              <div class="flex flex-col items-center">
                <h2 class="text-blue-800 mb-2">
                  {{ $t("blur_edit.side_image") }}
                </h2>
                <div
                  class="relative border-2 border-gray-600"
                  :style="getContainerStyle('side')"
                  @mousedown="deselectAll"
                >
                  <img
                    v-if="getImageSide"
                    ref="sideImage"
                    :src="getImageSide"
                    :style="getImageStyle('side')"
                    alt="Side image"
                    @load="onImageLoad('side', $event)"
                  />
                  <blur-box
                    v-if="sideBlurBox && sideImageDimensions"
                    :box="sideBlurBox"
                    :selected="selectedImage === 'side'"
                    :simple-mode="isSimpleMode"
                    :display-scale="getDisplayScale('side')"
                    :image-dimensions="sideImageDimensions"
                    :original-box="originalSideBox"
                    @select="selectedImage = 'side'"
                    @update="pendingSideBlurData = { faces: [$event] }"
                  />
                </div>
                <button
                  v-if="!sideBlurBox && sideImageDimensions && !isSimpleMode"
                  class="mt-2 py-2 px-4 bg-blue-800 text-white"
                  @click="addBlurBox('side')"
                >
                  {{ $t("blur_edit.add_blur_box") }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Right panel -->
        <div
          class="flex flex-col justify-between bg-gray-300 -m-4 p-4"
          style="width: 300px; min-width: 300px"
        >
          <div class="text-xl font-semibold text-blue-800 mb-4">
            {{ $t("blur_edit.title") }}
          </div>
          <blur-controls
            v-if="selectedImage && selectedBlurBox"
            :box="selectedBlurBox"
            :image-dimensions="selectedImageDimensions"
            :is-simple-mode="isSimpleMode"
            :original-box="selectedOriginalBox"
            @update="onControlsUpdate"
          />
          <div class="w-full bg-gray-200">
            <div class="flex mx-auto p-4">
              <div class="w-full grid grid-cols-2 gap-2">
                <button
                  :class="{
                    'col-span-2 button py-2 px-8 w-full text-white': true,
                    'bg-blue-800': hasPendingChanges || !isSaving,
                    'bg-gray-600': !hasPendingChanges || isSaving
                  }"
                  :disabled="!hasPendingChanges || isSaving"
                  @click="saveAllChanges"
                >
                  {{ isSaving ? $t("blur_edit.saving") : $t("blur_edit.save") }}
                </button>
                <button
                  :class="{
                    'col-span-2 button py-2 px-8 w-full text-white': true,
                    'bg-red-700': hasPendingChanges,
                    'bg-gray-400': !hasPendingChanges
                  }"
                  :disabled="!hasPendingChanges"
                  @click="resetChanges"
                >
                  {{ $t("blur_edit.reset") }}
                </button>
                <button
                  class="col-span-2 button py-2 px-8 w-full bg-yellow-700 text-white"
                  :disabled="isRejecting"
                  @click="showRejectDialog = true"
                >
                  {{
                    isRejecting
                      ? $t("blur_edit.rejecting")
                      : $t("blur_edit.reject")
                  }}
                </button>
                <button
                  :class="{
                    'col-span-2 button py-2 px-8 w-full text-white': true,
                    'bg-red-700': !isSaving,
                    'bg-gray-600': isSaving
                  }"
                  :disabled="isSaving"
                  @click="goBack"
                >
                  {{ $t("blur_edit.back") }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-else
      class="flex flex-col mx-auto p-4 w-full h-full items-center text-blue-800 text-2xl my-12"
    >
      {{ $t("blur_edit.loading") }}
    </div>
    <Dialog
      v-if="showRejectDialog"
      color="yellow"
      :title="$t('blur_edit.dialogs.reject.title').toString()"
      :message="$t('blur_edit.dialogs.reject.message').toString()"
      @confirmed="executeReject"
      @close="showRejectDialog = false"
    />
    <Dialog
      v-if="showBackDialog"
      color="red"
      :title="$t('blur_edit.dialogs.back.title').toString()"
      :message="$t('blur_edit.dialogs.back.message').toString()"
      @confirmed="resetStatusAndNavigate"
      @close="showBackDialog = false"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  ROUTE_BODYPRINT_EDIT,
  ROUTE_BLUR_EDIT,
  ROUTE_BLUR_QUEUE
} from "@/router";
import BlurControls from "@/components/blur/blur-controls.vue";
import BlurBox from "@/components/blur/blur-box.vue";
import Dialog from "@/components/dialog.vue";
import { clampBox } from "@/utils/blur";
import { BodyprintQueueStatus } from "@/enums/modules/BodyprintQueueStatus";

export default {
  name: "BodyprintBlurEditView",
  components: { BlurControls, BlurBox, Dialog },
  data() {
    return {
      isLoading: true,
      isSaving: false,
      isRejecting: false,
      showRejectDialog: false,
      showBackDialog: false,
      maxDisplayHeight: 800,
      pendingBackBlurData: null,
      pendingSideBlurData: null,
      // Original box dimensions (from server data, used to limit simple mode)
      originalBackBox: null,
      originalSideBox: null,
      // Actual image dimensions (detected on load)
      backImageDimensions: null,
      sideImageDimensions: null,
      // Selection state
      selectedImage: null
    };
  },
  computed: {
    ...mapGetters({
      getBodyPrint: "bodyprint/getBodyPrint",
      getQueueItem: "bodyprint/getQueueItem",
      getImageSide: "bodyprint/getImageSide",
      getImageBack: "bodyprint/getImageBack",
      getBlurDataBack: "bodyprint/getBlurDataBack",
      getBlurDataSide: "bodyprint/getBlurDataSide",
      getImageIdBack: "bodyprint/getImageIdBack",
      getImageIdSide: "bodyprint/getImageIdSide",
      getOriginalImageWidth: "bodyprint/getOriginalImageWidth",
      getOriginalImageHeight: "bodyprint/getOriginalImageHeight",
      getBlurPositionRatio: "bodyprint/getBlurPositionRatio",
      getBlurMaxBoxWidth: "bodyprint/getBlurMaxBoxWidth",
      getBlurMaxBoxHeight: "bodyprint/getBlurMaxBoxHeight",
      getBlurSimpleResizeMargin: "bodyprint/getBlurSimpleResizeMargin",
      getAuth: "auth/getAuth"
    }),
    isSimpleMode() {
      return (
        this.getAuth.blurEditSimple === true &&
        this.getAuth.blurEditFull !== true
      );
    },
    backBlurBox() {
      if (this.pendingBackBlurData) {
        return this.pendingBackBlurData.faces[0] ?? null;
      }
      if (this.getBlurDataBack?.faces?.length > 0) {
        return this.normalizeServerBox(
          this.getBlurDataBack.faces[0],
          this.backImageDimensions
        );
      }
      return null;
    },
    sideBlurBox() {
      if (this.pendingSideBlurData) {
        return this.pendingSideBlurData.faces[0] ?? null;
      }
      if (this.getBlurDataSide?.faces?.length > 0) {
        return this.normalizeServerBox(
          this.getBlurDataSide.faces[0],
          this.sideImageDimensions
        );
      }
      return null;
    },
    hasPendingChanges() {
      return (
        this.pendingBackBlurData !== null || this.pendingSideBlurData !== null
      );
    },
    selectedBlurBox() {
      return this.selectedImage === "back"
        ? this.backBlurBox
        : this.sideBlurBox;
    },
    selectedImageDimensions() {
      return this.selectedImage === "back"
        ? this.backImageDimensions
        : this.sideImageDimensions;
    },
    selectedOriginalBox() {
      return this.selectedImage === "back"
        ? this.originalBackBox
        : this.originalSideBox;
    }
  },
  watch: {
    getBlurDataBack: {
      immediate: true,
      handler(data) {
        if (!this.originalBackBox && data?.faces?.length > 0) {
          this.originalBackBox = this.normalizeServerBox(data.faces[0], null);
        }
      }
    },
    getBlurDataSide: {
      immediate: true,
      handler(data) {
        if (
          !this.originalSideBox &&
          data &&
          data.faces &&
          data.faces.length > 0
        ) {
          this.originalSideBox = this.normalizeServerBox(data.faces[0], null);
        }
      }
    }
  },
  created() {
    this.$store.dispatch("bodyprint/fetchBlurResizeMarginSetting").then(() => {
      this.loadData();
    });
  },
  methods: {
    normalizeServerBox(face, imageDimensions) {
      return clampBox(
        { ...face },
        {
          imageDimensions,
          isSimpleMode: false,
          originalBox: null,
          simpleResizeMargin: this.getBlurSimpleResizeMargin,
          maxBoxWidth: this.getBlurMaxBoxWidth,
          maxBoxHeight: this.getBlurMaxBoxHeight,
          maxPositionRatio: this.getBlurPositionRatio
        }
      );
    },
    addBlurBox(imageType) {
      const box = this.getDefaultBox(imageType);
      if (!box) return;
      if (imageType === "back") {
        this.pendingBackBlurData = { faces: [box] };
      } else {
        this.pendingSideBlurData = { faces: [box] };
      }
      this.selectedImage = imageType;
    },
    removeBlurBox() {
      this.pendingBackBlurData = { faces: [] };
      if (this.selectedImage === "back") {
        this.selectedImage = null;
      }
    },
    resetChanges() {
      this.pendingBackBlurData = null;
      this.pendingSideBlurData = null;
    },
    deselectAll() {
      this.selectedImage = null;
    },
    onControlsUpdate(newBox) {
      if (this.selectedImage === "back") {
        this.pendingBackBlurData = { faces: [newBox] };
      } else {
        this.pendingSideBlurData = { faces: [newBox] };
      }
    },
    getDefaultBox(imageType) {
      const dimensions =
        imageType === "back"
          ? this.backImageDimensions
          : this.sideImageDimensions;
      if (!dimensions) return null;

      const boxWidth = 100;
      const boxHeight = 100;
      const maxY = dimensions.height * this.getBlurPositionRatio;

      return {
        x: Math.round((dimensions.width - boxWidth) / 2),
        y: Math.round((maxY - boxHeight) / 2),
        width: boxWidth,
        height: boxHeight
      };
    },
    onImageLoad(imageType, event) {
      const img = event.target;
      const dimensions = {
        width: img.naturalWidth,
        height: img.naturalHeight
      };
      if (imageType === "back") {
        this.backImageDimensions = dimensions;
      } else {
        this.sideImageDimensions = dimensions;
      }
    },
    getDisplayScale(imageType) {
      const dimensions =
        imageType === "back"
          ? this.backImageDimensions
          : this.sideImageDimensions;
      if (!dimensions) return 1;
      return this.maxDisplayHeight / dimensions.height;
    },
    getContainerStyle(imageType) {
      const dimensions =
        imageType === "back"
          ? this.backImageDimensions
          : this.sideImageDimensions;
      if (!dimensions) {
        return { width: "400px", height: this.maxDisplayHeight + "px" };
      }
      const scale = this.getDisplayScale(imageType);
      return {
        width: dimensions.width * scale + "px",
        height: dimensions.height * scale + "px",
        overflow: "hidden"
      };
    },
    getImageStyle(imageType) {
      const dimensions =
        imageType === "back"
          ? this.backImageDimensions
          : this.sideImageDimensions;
      if (!dimensions) {
        return { maxHeight: this.maxDisplayHeight + "px" };
      }
      const scale = this.getDisplayScale(imageType);
      return {
        width: dimensions.width * scale + "px",
        height: dimensions.height * scale + "px"
      };
    },
    loadData() {
      const queueId = this.$route.params.queue_id;
      this.$store
        .dispatch("bodyprint/requestBodyprintToBlur", { id: queueId })
        .then(() => {
          const bp = this.$store.getters["bodyprint/getBodyPrint"];
          if (bp.user) {
            this.$store
              .dispatch("bodyprint/getBlurredImages", bp.user)
              .then(() => {
                this.$store.dispatch("bodyprint/checkBodyprint").then(() => {
                  this.isLoading = false;
                });
              })
              .catch(() => {
                this.goBack();
              });
          }
        })
        .catch(() => {
          this.goBack();
        });
    },
    async saveAllChanges() {
      this.isSaving = true;
      const userId = this.getBodyPrint.user;
      const queueId = this.$route.params.queue_id;

      try {
        if (this.pendingBackBlurData) {
          await this.$store.dispatch("bodyprint/updateBlurData", {
            imageId: this.getImageIdBack,
            faces: this.pendingBackBlurData.faces,
            userId: userId
          });
          this.pendingBackBlurData = null;
        }

        if (this.pendingSideBlurData) {
          await this.$store.dispatch("bodyprint/updateBlurData", {
            imageId: this.getImageIdSide,
            faces: this.pendingSideBlurData.faces,
            userId: userId
          });
          this.pendingSideBlurData = null;
        }

        await this.$store
          .dispatch("bodyprint/updateBlurStatusAuto", queueId)
          .then(() => {
            // Update queue item status to IN_PROGRESS
            this.$store.dispatch("bodyprint/setQueueItemStatus", {
              status: BodyprintQueueStatus.IN_PROGRESS
            });

            this.$store.dispatch("bodyprint/requestBodyprintToBlur", {
              id: queueId
            });
          });
      } catch (error) {
        console.error("Failed to save blur data:", error);
      } finally {
        this.isSaving = false;
      }
    },
    executeReject() {
      this.showRejectDialog = false;
      this.isRejecting = true;
      try {
        const { step, measure_step, projection } = this.$route.query;
        const comingFromBodyprintEdit = !!(
          step ||
          projection ||
          measure_step !== undefined
        );
        this.$store
          .dispatch("bodyprint/updateBlurStatus", {
            queueId: this.$route.params.queue_id,
            blurStatus: "REJECTED",
            updateOriginalBodyprint: !comingFromBodyprintEdit
          })
          .then(() => {
            if (comingFromBodyprintEdit) {
              this.$store
                .dispatch("bodyprint/requestBodyprint", {
                  id: this.$route.params.queue_id
                })
                .then(() => {
                  this.navigateBack();
                });
            } else {
              this.navigateBack();
            }
          });
      } catch (error) {
        console.error("Failed to reject blur:", error);
      } finally {
        this.isRejecting = false;
      }
    },
    goBack() {
      if (this.hasPendingChanges) {
        this.showBackDialog = true;
      } else {
        this.resetStatusAndNavigate();
      }
    },
    resetStatusAndNavigate() {
      const queueItem = this.$store.getters["bodyprint/getQueueItem"];

      this.$store
        .dispatch("bodyprint/updateBlurStatus", {
          queueId: this.$route.params.queue_id,
          blurStatus: queueItem.previous_blur_status
        })
        .then(() => {
          this.navigateBack();
        });
    },
    navigateBack() {
      this.showBackDialog = false;
      if (this.$route.name === ROUTE_BLUR_EDIT) {
        this.$router.push({ name: ROUTE_BLUR_QUEUE });
      } else {
        this.$router.push({
          name: ROUTE_BODYPRINT_EDIT,
          params: { queue_id: this.$route.params.queue_id },
          query: this.$route.query
        });
      }
    }
  }
};
</script>
